// Billing and contract constants - restricted to month, quarter, year only (month-based)

export const BILLING_CYCLE_OPTIONS = [
  { value: '1_month', label: 'Monthly' },
  { value: '3_months', label: 'Quarterly' },
  { value: '6_months', label: 'Semi-Annually' },
  { value: '12_months', label: 'Yearly' },
  { value: '24_months', label: 'Bi-Annually' }
] as const;

export const CONTRACT_LENGTH_OPTIONS = [
  { value: '1_month', label: 'One Month' },
  { value: '3_months', label: 'Three Months' },
  { value: '6_months', label: 'Six Months' },
  { value: '12_months', label: 'One Year' },
  { value: '24_months', label: 'Two Years' }
] as const;

export const NOTICE_PERIOD_OPTIONS = [
  { value: '1_month', label: 'One Month' },
  { value: '2_months', label: 'Two Months' },
  { value: '3_months', label: 'Three Months' },
  { value: '6_months', label: 'Six Months' }
] as const;

// Type definitions for the allowed values
export type BillingCycleValue = '1_month' | '3_months' | '6_months' | '12_months' | '24_months';
export type ContractLengthValue = '1_month' | '3_months' | '6_months' | '12_months' | '24_months';
export type NoticePeriodValue = '1_month' | '2_months' | '3_months' | '6_months';

// Contract-specific types (more restrictive)
export type ContractBillingCycleValue = '1_month' | '3_months' | '12_months';
export type ContractLengthValueRestricted = '1_month' | '3_months' | '12_months';

// Validation functions
export const isValidBillingCycle = (value: string): value is BillingCycleValue => {
  return ['1_month', '3_months', '6_months', '12_months', '24_months'].includes(value);
};

export const isValidContractLength = (value: string): value is ContractLengthValue => {
  return ['1_month', '3_months', '6_months', '12_months', '24_months'].includes(value);
};

export const isValidNoticePeriod = (value: string): value is NoticePeriodValue => {
  return ['1_month', '2_months', '3_months', '6_months'].includes(value);
};

// Contract-specific validation functions
export const isValidContractBillingCycle = (value: string): value is ContractBillingCycleValue => {
  return ['1_month', '3_months', '12_months'].includes(value);
};

export const isValidContractLengthRestricted = (value: string): value is ContractLengthValueRestricted => {
  return ['1_month', '3_months', '12_months'].includes(value);
};

// Helper functions to get display labels
export const getBillingCycleLabel = (value: BillingCycleValue): string => {
  const option = BILLING_CYCLE_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

export const getContractLengthLabel = (value: ContractLengthValue): string => {
  const option = CONTRACT_LENGTH_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

export const getNoticePeriodLabel = (value: NoticePeriodValue): string => {
  const option = NOTICE_PERIOD_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

// Default values
export const DEFAULT_BILLING_CYCLE: BillingCycleValue = '1_month';
export const DEFAULT_CONTRACT_LENGTH: ContractLengthValue = '12_months';
export const DEFAULT_NOTICE_PERIOD: NoticePeriodValue = '1_month';